import { ApolloError } from "@apollo/client";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";
import { useEffect, useState } from "react";
import { useUserContext } from "../../../common/components/UserContextProvider";
import {
    DEFAULT_LOGIC_OPERATOR,
    getContextDatasets,
} from "../../filter-criteria/utils/utils";
import { getDefaultDatasetConfiguration } from "./utils/general-utils";
import { useMap } from "./map";
import {
    MapContextDataset,
    MapContextPolygon,
    MapContextPolygonSource,
} from "./types";
import {
    InputMapTemplateDataset,
    InputViewBox,
    SubscriptionResponse,
    useFetchMapTemplateDatasetsExtendedLazyQuery,
    useFetchSavedViewDatasetsLazyQuery,
    useFetchUserDataSamplesLazyQuery,
} from "@biggeo/bg-server-lib/studio";
import {
    getRandomColor,
    toNonReadonlyArray,
    WithRequiredProperty,
} from "@biggeo/bg-utils";
import compact from "lodash/compact";
import {
    getAreaOutsideBoundaries,
    getMapDrawnFeatures,
    getMapFeatures,
    getPolygonsThatOverlapOtherPolygons,
    snapToView,
} from "../../utils/geometry-utils";
import { FunctionType } from "../../utils/type";
import isNil from "lodash/isNil";
import { v4 as uuid } from "uuid";
import { getConflictAreas } from "../../utils/utils";
import { useCloseModal, useOpenModal } from "../../../common/redux/hooks";
import ConflictAreasModal from "../views/ConflictAreasModal";
import { isLayerPresent, isSourcePresent } from "../utils/data-layers-utils";
import {
    BOUNDARIES_ADDITIONAL_OUTLINE_LAYER_ID,
    BOUNDARIES_FILL_LAYER_ID,
    BOUNDARIES_OUTLINE_LAYER_ID,
    BOUNDARIES_OUTSIDE_AREA_SOURCE_ID,
    BOUNDARIES_SOURCE_ID,
    OUTSIDE_AREA_LAYER_ID,
} from "../utils/boundaries-layers-utils";
import {
    DEFAULT_SHAPE_COLOR,
    DEFAULT_SHAPE_OPACITY,
} from "../hooks/style-hooks";
import { GeoJSONSource } from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";

export const useMapTemplateDatasets = (
    input: InputMapTemplateDataset,
    options?: Partial<{ skip: boolean }>
): {
    datasets: MapContextDataset[];
    loading: boolean;
    error?: ApolloError;
} => {
    const { dispatch, datasets } = useMap();

    const skip = options && !isUndefined(options.skip) ? options.skip : false;
    const [isLoading, setIsLoading] = useState(!skip && isEmpty(datasets));
    const [data, setData] = useState<MapContextDataset[]>([]);

    const {
        executeQuery: getMapTemplateDatasets,
        queryReturn: [_, { loading, error }],
    } = useFetchMapTemplateDatasetsExtendedLazyQuery();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (!skip) {
            getMapTemplateDatasets({
                variables: { input },
                onCompleted: (data) => {
                    setIsLoading(false);

                    const values: MapContextDataset[] = pipe(
                        data.fetchMapTemplateDatasetsExtended,
                        toNonReadonlyArray,
                        A.map((d) => ({
                            dataSource: d.dataSource,
                            mapTemplateDataset: d.mapTemplateDataset,
                            dataSourceConfiguration: d.dataSourceConfiguration,
                            geographyType:
                                d.etlDatasetLocation?.geographyType ||
                                undefined,
                            isSelected: false,
                            isVisible: false,
                            isGettingStyled: false,
                            isLegendOpen: false,
                            isTableViewed: false,
                            configuration: getDefaultDatasetConfiguration(),
                            filters: {
                                logicOperator: DEFAULT_LOGIC_OPERATOR,
                                filters: [],
                            },
                            selectedRows: [],
                            pinnedRows: [],
                            isSample: false,
                            fkMarketplaceDatasetId: undefined,
                            organization: undefined,
                            image: undefined,
                        }))
                    );

                    if (dispatch) {
                        dispatch({
                            type: "ADD_DATASETS",
                            values,
                        });
                    }

                    setData(values);
                },
            });
        }
    }, [skip, input.fkMapTemplateId, input.mapUse]);

    return { datasets: data, loading: loading || isLoading, error };
};

export const useSavedViewDatasets = (
    id: string,
    options?: Partial<{ skip: boolean }>
): {
    datasets: MapContextDataset[];
    loading: boolean;
    error?: ApolloError;
} => {
    const { dispatch, datasets } = useMap();

    const skip = options && !isUndefined(options.skip) ? options.skip : false;
    const [isLoading, setIsLoading] = useState(!skip && isEmpty(datasets));
    const [data, setData] = useState<MapContextDataset[]>([]);

    const {
        executeQuery: getSavedView,
        queryReturn: [_, { loading, error }],
    } = useFetchSavedViewDatasetsLazyQuery();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (!skip) {
            getSavedView({
                variables: { id },
                notifyOnNetworkStatusChange: true,
                onCompleted: ({ fetchSavedViewDatasets: data }) => {
                    setIsLoading(false);

                    const values = getContextDatasets({
                        savedViewDatasets: pipe(
                            data,
                            toNonReadonlyArray,
                            A.map((d) => {
                                const dataset = datasets.find(
                                    (item) =>
                                        item.dataSource.id === d.dataSource.id
                                );

                                return dataset
                                    ? {
                                          ...d,
                                          selectedRows: dataset.selectedRows,
                                          pinnedRows: dataset.pinnedRows,
                                          filters: dataset.filters,
                                      }
                                    : d;
                            })
                        ),
                    });

                    if (dispatch) {
                        dispatch({
                            type: "ADD_DATASETS",
                            values,
                        });
                    }

                    setData(values);
                },
            });
        }
    }, [skip, id]);

    return { datasets: data, loading: loading || isLoading, error };
};

export const useSampleDatasets = (
    options?: Partial<{ skip: boolean }>
): {
    samples: MapContextDataset[];
    loading: boolean;
    error?: ApolloError;
} => {
    const { dispatch, datasets } = useMap();
    const { organization } = useUserContext();

    const samples = datasets.filter((d) => !!d.isSample);

    const [data, setData] = useState<MapContextDataset[]>(samples || []);
    const skip = options && !isUndefined(options.skip) ? options.skip : false;
    const [isLoading, setIsLoading] = useState(!skip && isEmpty(samples));

    const {
        executeQuery: getUserDataSamples,
        queryReturn: [_, { loading, error }],
    } = useFetchUserDataSamplesLazyQuery();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (!skip) {
            getUserDataSamples({
                onCompleted: (data) => {
                    setIsLoading(false);

                    const values: MapContextDataset[] = pipe(
                        data.fetchUserDataSamples,
                        toNonReadonlyArray,
                        A.map((sample) => {
                            const sampleId = sample.userDataSource.sampleId;
                            const fkUserId = sample.userDataSource.fkUserId;

                            return sampleId && fkUserId
                                ? {
                                      dataSource: sample.dataSource,
                                      mapTemplateDataset: undefined,
                                      dataSourceConfiguration:
                                          sample.dataSourceConfiguration,
                                      geographyType: undefined,
                                      isSelected: false,
                                      isVisible: false,
                                      isGettingStyled: false,
                                      isLegendOpen: false,
                                      isTableViewed: false,
                                      configuration:
                                          getDefaultDatasetConfiguration(),
                                      filters: {
                                          logicOperator: DEFAULT_LOGIC_OPERATOR,
                                          filters: [],
                                      },
                                      selectedRows: [],
                                      pinnedRows: [],
                                      isSample: true,
                                      fkMarketplaceDatasetId:
                                          sample.marketplaceDataset?.id,
                                      organization: sample.organization.name,
                                      image:
                                          sample.marketplaceDataset?.img ??
                                          sample.organization.img ??
                                          undefined,
                                      color: getRandomColor(),
                                  }
                                : undefined;
                        }),
                        compact
                    );

                    setData(values);

                    if (dispatch) {
                        dispatch({
                            type: "ADD_DATASETS",
                            values: values,
                        });
                    }
                },
            });
        }
    }, [skip, organization?.id]);

    return { samples: data, loading: loading || isLoading, error };
};

/**
 *
 * A custom hook that manages the polygons state from the Context.
 * @returns Helper functions that mutates polygons:
 *      deletePolygon, addPolygon, updatePolygon, clearPolygons, overridePolygons & clearSelectedPolygons
 *
 */
export const usePolygon = (): {
    deletePolygon: (id: string) => void;
    addPolygon: (values: MapContextPolygon) => void;
    updatePolygon: (
        values: WithRequiredProperty<Partial<MapContextPolygon>, "id">
    ) => void;
    clearPolygons: () => void;
    overridePolygons: (polygons: MapContextPolygon[]) => void;
    clearSelectedPolygons: () => void;
} => {
    const { dispatch, draw, polygons } = useMap();

    const deletePolygon = (id: string) => {
        // 1. Delete from Context
        dispatch?.({
            type: "DELETE_POLYGONS",
            values: { ids: [id] },
        });

        if (draw.current) {
            // Delete from Draw state
            draw.current.delete(id);
        }
    };

    const addPolygon = (values: MapContextPolygon) => {
        dispatch?.({
            type: "ADD_POLYGONS",
            values,
        });
    };

    const updatePolygon = (
        values: WithRequiredProperty<Partial<MapContextPolygon>, "id">
    ) => {
        dispatch?.({
            type: "UPDATE_POLYGON",
            values,
        });
    };

    const clearPolygons = () => {
        dispatch?.({
            type: "CLEAR_POLYGONS",
        });
    };

    const overridePolygons = (polygons: MapContextPolygon[]) => {
        dispatch?.({
            type: "OVERWRITE_POLYGONS",
            values: polygons,
        });
    };

    const clearSelectedPolygons = () => {
        polygons.map((p) => {
            if (
                p.isSelected &&
                p.source === MapContextPolygonSource.drawnShape
            ) {
                updatePolygon({ id: p.id, isSelected: false });
            }
        });
    };

    return {
        deletePolygon,
        addPolygon,
        updatePolygon,
        clearPolygons,
        overridePolygons,
        clearSelectedPolygons,
    };
};

/**
 * A custom hook that provides utility functions for managing map state.
 * This hook encapsulates various dispatch actions for updating the map's state,
 * including loading status, function type, viewport.
 */
export const useMapStateUtilities = (): {
    setLoading: (
        i: Partial<{
            data: boolean;
            boundaries: boolean;
        }>
    ) => void;
    setFunctionType: (f: FunctionType) => void;
    setViewport: (p: InputViewBox) => void;
    setSocketResponses: (responses: SubscriptionResponse) => void;
} => {
    const { dispatch } = useMap();

    const setLoading = (i: Partial<{ data: boolean; boundaries: boolean }>) => {
        dispatch?.({
            type: "SET_LOADING",
            values: { state: i },
        });
    };

    const setFunctionType = (f: FunctionType) => {
        dispatch?.({
            type: "SET_MAP",
            values: { functionType: f },
        });
    };

    const setViewport = (p: InputViewBox) => {
        dispatch?.({
            type: "SET_MAP",
            values: { viewport: p },
        });
    };

    const setSocketResponses = (responses: SubscriptionResponse) => {
        dispatch?.({
            type: "SET_SOCKET_RESPONSES",
            values: responses,
        });
    };

    return {
        setLoading,
        setFunctionType,
        setViewport,
        setSocketResponses,
    };
};

/**
 *
 * A custom hook that manages the Saved Boundaries in polygons state from the Context.
 * A Saved Boundary will be added to polygons with `source` as `MapContextPolygonSource.boundary`.
 * @op Optional - Sets the Boundaries layers and sources on the Map if `style` is true in input.
 * @param input.recenter - Recenters the Map on the current selected Boundaries.
 * @param input.style - Sets the Boundaries layers and sources on the Map.
 * @returns Helper functions that mutates polygons and optionally sets the layers & sources on the Map:
 *          selectBoundaries & unselectBoundaries.
 *
 */
export const useSavedBoundaries = (
    input: Partial<{ recenter: boolean; style: boolean }>
): {
    selectBoundaries: (i: {
        boundaries: GeoJSON.Feature<
            GeoJSON.Geometry,
            GeoJSON.GeoJsonProperties
        >[];
        skipConflicts?: boolean;
    }) => void;
    unselectBoundaries: (boundaryIds: readonly string[]) => void;
    handleConflictAreas: (input: {
        onClickAccept: () => void;
        onClickDecline: () => void;
        boundaries: MapContextPolygon[];
    }) => void;
} => {
    const { recenter = true, style = true } = input;

    const openModal = useOpenModal();
    const closeModal = useCloseModal();
    const { dispatch, draw, isMapLoaded, polygons, map } = useMap();

    const { setFunctionType } = useMapStateUtilities();

    const {
        addBoundarySourcesAndLayers,
        setBoundarySourcesAndLayers,
        updateBoundarySources,
    } = useSavedBoundariesStyle();

    const drawnPolygons = getMapFeatures(draw, isMapLoaded);

    const setSavedBoundaries = (values: MapContextPolygon[]) => {
        dispatch?.({ type: "ADD_POLYGONS", values });
    };

    const unselectBoundaries = (boundaryIds: readonly string[]) => {
        const allBoundaries = polygons.filter(
            (polygon) => polygon.source === MapContextPolygonSource.boundary
        );

        const boundaries = polygons.filter(
            (polygon) => !!boundaryIds.includes(polygon.id)
        );

        const conflicts =
            !isEmpty(drawnPolygons) && !isEmpty(boundaries)
                ? getPolygonsThatOverlapOtherPolygons(
                      drawnPolygons,
                      boundaries.map((b) => b.feature)
                  )
                : [];

        // There are conflicts + There are other selected boundaries
        const isConflict = !isEmpty(conflicts) && allBoundaries.length;

        dispatch?.({
            type: "DELETE_POLYGONS",
            values: { ids: boundaryIds },
        });

        if (isConflict) {
            allBoundaries.map((b) => {
                dispatch?.({
                    type: "UPDATE_POLYGON",
                    values: { ...b, isSelected: false },
                });
            });
        }

        if (style && map.current) {
            updateBoundarySources({
                map: map.current,
                data: polygons
                    .filter(
                        (polygon) =>
                            !boundaryIds.includes(polygon.id) &&
                            polygon.source === MapContextPolygonSource.boundary
                    )
                    .map((b) => b.feature),
                recenter,
            });
        }
    };

    const selectBoundaries = (i: {
        boundaries: GeoJSON.Feature<
            GeoJSON.Geometry,
            GeoJSON.GeoJsonProperties
        >[];
        skipConflicts?: boolean;
    }) => {
        const { boundaries, skipConflicts = false } = i;

        const allBoundaries = polygons.filter(
            (polygon) => polygon.source === MapContextPolygonSource.boundary
        );

        const features = boundaries.map((feature) => {
            return {
                id: feature.properties?.fkSavedAreaId
                    ? feature.properties.fkSavedAreaId
                    : (feature.id ?? uuid()),
                feature,
                source: MapContextPolygonSource.boundary,
                isSelected: true,
            };
        });

        if (skipConflicts) {
            setSavedBoundaries(features);
        } else {
            const conflicts =
                !isEmpty(drawnPolygons) && !isEmpty(boundaries)
                    ? drawnPolygons.map(
                          (shape) =>
                              getConflictAreas({
                                  shape,
                                  boundaries: boundaries,
                              }).intersection
                      )
                    : [];

            const isConflict = !isEmpty(conflicts) && isEmpty(allBoundaries);

            setSavedBoundaries(
                isConflict
                    ? features.map((f) => ({ ...f, isSelected: false }))
                    : features
            );
        }

        if (style && map.current && isMapLoaded) {
            addBoundarySourcesAndLayers(map.current);
            setBoundarySourcesAndLayers({
                map: map.current,
                features: [
                    ...allBoundaries.map((f) => f.feature),
                    ...features.map((f) => f.feature),
                ],
                recenter,
            });
        }
    };

    const handleConflictAreas = (input: {
        onClickAccept: () => void;
        onClickDecline: () => void;
    }) => {
        const { onClickAccept, onClickDecline } = input;

        const boundaries = polygons.filter(
            (p) => p.source === MapContextPolygonSource.boundary
        );

        openModal({
            modalType: "new-dialog",
            component: (
                <ConflictAreasModal
                    onClickDecline={() => {
                        onClickDecline();

                        unselectBoundaries(boundaries.map((b) => b.id));

                        closeModal();
                    }}
                    onClickAccept={() => {
                        // Discard conflict areas if applicable
                        onClickAccept();

                        // Reset function type to savedPolygon
                        setFunctionType(FunctionType.savedPolygon);

                        // Reset conflict state to false
                        polygons.map((polygon) => {
                            if (
                                polygon.source ===
                                MapContextPolygonSource.boundary
                            ) {
                                dispatch?.({
                                    type: "UPDATE_POLYGON",
                                    values: { ...polygon, isSelected: true },
                                });
                            }
                        });

                        closeModal();
                    }}
                />
            ),

            dialogProps: {
                variant: "centered-small",
                onClose: undefined,
            },
        });
    };
    return {
        selectBoundaries,
        unselectBoundaries,
        handleConflictAreas,
    };
};

/**
 *
 * A custom hook that manages the Boundaries's layers and sources on the Map.
 * @returns Helper functions that updates the Map's layers and sources: addBoundarySourcesAndLayers, setBoundarySourcesAndLayers, removeBoundarySourcesAndLayers & updateBoundarySources
 *
 */
export const useSavedBoundariesStyle = (): {
    addBoundarySourcesAndLayers: (map: mapboxgl.Map) => void;
    setBoundarySourcesAndLayers: (i: {
        map: mapboxgl.Map;
        features: GeoJSON.Feature<
            GeoJSON.Geometry,
            GeoJSON.GeoJsonProperties
        >[];
        recenter?: boolean;
    }) => void;
    removeBoundarySourcesAndLayers: (map: mapboxgl.Map) => void;
    updateBoundarySources: (input: {
        map: mapboxgl.Map;
        data: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[];
        recenter?: boolean;
    }) => void;
} => {
    const { draw, isMapLoaded } = useMap();

    const drawnPolygons = getMapFeatures(draw, isMapLoaded);

    const addBoundarySourcesAndLayers = (map: mapboxgl.Map) => {
        // 1. Adding needed sources
        if (
            !isSourcePresent({
                sourceId: BOUNDARIES_SOURCE_ID,
                isMapLoaded,
                map,
            })
        ) {
            map.addSource(BOUNDARIES_SOURCE_ID, {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: [],
                },
            });
        }

        if (
            !isSourcePresent({
                sourceId: BOUNDARIES_OUTSIDE_AREA_SOURCE_ID,
                isMapLoaded,
                map,
            })
        ) {
            map.addSource(BOUNDARIES_OUTSIDE_AREA_SOURCE_ID, {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: [],
                },
            });
        }

        // 2. Adding needed layers
        if (
            !isLayerPresent({
                layerId: OUTSIDE_AREA_LAYER_ID,
                isMapLoaded,
                map,
            })
        ) {
            map.addLayer({
                id: OUTSIDE_AREA_LAYER_ID,
                type: "fill",
                source: BOUNDARIES_OUTSIDE_AREA_SOURCE_ID,
                interactive: false,
                paint: {
                    "fill-color": "#D0D5DD",
                    "fill-opacity": 0.5,
                },
            });
        }

        if (
            !isLayerPresent({
                layerId: BOUNDARIES_OUTLINE_LAYER_ID,
                isMapLoaded,
                map,
            })
        ) {
            map.addLayer({
                id: BOUNDARIES_OUTLINE_LAYER_ID,
                type: "line",
                source: BOUNDARIES_SOURCE_ID,
                interactive: false,
                paint: {
                    "line-color": [
                        "coalesce",
                        ["get", "stroke-color"],
                        DEFAULT_SHAPE_COLOR,
                    ],
                    "line-width": ["coalesce", ["get", "stroke-width"], 2],
                    "line-opacity": ["coalesce", ["get", "stroke-opacity"], 1],
                },
            });
        }

        // An additional invisible outline to stop the `not-allowed`
        // cursor from showing once it gets close to a saved area.
        if (
            !isLayerPresent({
                layerId: BOUNDARIES_ADDITIONAL_OUTLINE_LAYER_ID,
                isMapLoaded,
                map,
            })
        ) {
            map.addLayer({
                id: BOUNDARIES_ADDITIONAL_OUTLINE_LAYER_ID,
                type: "line",
                source: BOUNDARIES_SOURCE_ID,
                interactive: false,
                paint: {
                    "line-color": DEFAULT_SHAPE_COLOR,
                    "line-width": 10,
                    "line-opacity": 0,
                },
            });
        }

        if (
            !isLayerPresent({
                layerId: BOUNDARIES_FILL_LAYER_ID,
                isMapLoaded,
                map,
            })
        ) {
            map.addLayer({
                id: BOUNDARIES_FILL_LAYER_ID,
                type: "fill",
                source: BOUNDARIES_SOURCE_ID,
                interactive: false,
                paint: {
                    "fill-color": [
                        "coalesce",
                        ["get", "fill-color"],
                        DEFAULT_SHAPE_COLOR,
                    ],
                    "fill-opacity": [
                        "coalesce",
                        ["get", "fill-opacity"],
                        DEFAULT_SHAPE_OPACITY,
                    ],
                },
            });
        }
    };

    const setBoundarySourcesAndLayers = (i: {
        map: mapboxgl.Map;
        features: GeoJSON.Feature<
            GeoJSON.Geometry,
            GeoJSON.GeoJsonProperties
        >[];
        recenter?: boolean;
    }) => {
        const { map, features, recenter = true } = i;

        const boundariesSource = map.getSource(
            BOUNDARIES_SOURCE_ID
        ) as GeoJSONSource;

        const outsideAreaSource = map.getSource(
            BOUNDARIES_OUTSIDE_AREA_SOURCE_ID
        ) as GeoJSONSource;

        const outsideArea = getAreaOutsideBoundaries(features);

        if (boundariesSource) {
            boundariesSource.setData({
                type: MapboxDraw.constants.geojsonTypes.FEATURE_COLLECTION,
                features,
            });
        }

        if (outsideAreaSource && outsideArea) {
            outsideAreaSource.setData(outsideArea);
        }

        if (recenter) {
            snapToView({
                features: [...features, ...drawnPolygons],
                map: { current: map },
            });
        }
    };

    const removeBoundarySourcesAndLayers = (map: mapboxgl.Map) => {
        // 1. Removing sources
        if (
            isSourcePresent({
                sourceId: BOUNDARIES_SOURCE_ID,
                isMapLoaded,
                map,
            })
        ) {
            map.removeSource(BOUNDARIES_SOURCE_ID);
        }

        if (
            isSourcePresent({
                sourceId: BOUNDARIES_OUTSIDE_AREA_SOURCE_ID,
                isMapLoaded,
                map,
            })
        ) {
            map.removeSource(BOUNDARIES_OUTSIDE_AREA_SOURCE_ID);
        }

        if (
            isLayerPresent({
                layerId: OUTSIDE_AREA_LAYER_ID,
                isMapLoaded,
                map,
            })
        ) {
            // 2. Removing layers
            map.removeLayer(OUTSIDE_AREA_LAYER_ID);
        }

        if (
            isLayerPresent({
                layerId: BOUNDARIES_OUTLINE_LAYER_ID,
                isMapLoaded,
                map,
            })
        ) {
            map.removeLayer(BOUNDARIES_OUTLINE_LAYER_ID);
        }

        if (
            isLayerPresent({
                layerId: BOUNDARIES_ADDITIONAL_OUTLINE_LAYER_ID,
                isMapLoaded,
                map,
            })
        ) {
            map.removeLayer(BOUNDARIES_ADDITIONAL_OUTLINE_LAYER_ID);
        }

        if (
            isLayerPresent({
                layerId: BOUNDARIES_FILL_LAYER_ID,
                isMapLoaded,
                map,
            })
        ) {
            map.removeLayer(BOUNDARIES_FILL_LAYER_ID);
        }
    };

    const updateBoundarySources = (input: {
        map: mapboxgl.Map;
        data: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[];
        recenter?: boolean;
    }) => {
        const { map, data, recenter = true } = input;

        const outsideArea = getAreaOutsideBoundaries(data);

        const boundariesSource = isSourcePresent({
            sourceId: BOUNDARIES_SOURCE_ID,
            isMapLoaded,
            map,
        });

        const outsideAreaSource = isSourcePresent({
            sourceId: BOUNDARIES_OUTSIDE_AREA_SOURCE_ID,
            isMapLoaded,
            map,
        });

        if (boundariesSource) {
            boundariesSource.setData({
                type: "FeatureCollection",
                features: data,
            });
        }

        if (outsideAreaSource) {
            if (outsideArea) {
                outsideAreaSource.setData(outsideArea);
            } else {
                outsideAreaSource.setData({
                    type: "FeatureCollection",
                    features: [],
                });
            }
        }

        if (recenter && data.length !== 0) {
            snapToView({
                features: [...data, ...drawnPolygons],
                map: { current: map },
            });
        }
    };

    return {
        addBoundarySourcesAndLayers,
        setBoundarySourcesAndLayers,
        removeBoundarySourcesAndLayers,
        updateBoundarySources,
    };
};
